import {
  MenuItem,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { TertiaryButton } from "components/Button";
import { taskActionLabel } from "features/Resources/Task/helpers";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { MdOutlineTouchApp } from "react-icons/md";
import { Task, TaskId } from "features/Resources/Task/models";
import { useNavigate, useSearchParams } from "react-router";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "helpers/url";
import TaskActionButton from "features/Resources/Task/Components/Button/TaskActionButton";
import { useMarkCompleteTask } from "features/Resources/Task/hooks";
import { NO_ACTION_STATUSES } from "features/Resources/Task/constants";

// complete_form tasks own a dedicated page (multi-field forms don't fit a
// modal well). Everything else uses the ?task_action=<id> modal pattern.
export function useOpenTaskAction() {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  return useCallback(
    (taskId: TaskId, taskAction: Task["taskAction"]) => {
      if (taskAction === "complete_form") {
        navigate(generatePath({ path: SHARED_ROUTES.formResponse, params: { taskId } }));
        return;
      }
      setSearchParams({ task_action: taskId });
    },
    [navigate, setSearchParams],
  );
}

export function TaskActionTertiaryButton({ taskId, taskAction }: { taskId: TaskId, taskAction: Task["taskAction"] }) {
  const openTaskAction = useOpenTaskAction();
  const label = taskActionLabel(taskAction);

  return (
    <TertiaryButton h="28px" onClick={() => openTaskAction(taskId, taskAction)}>
      {label}
    </TertiaryButton>
  );
}

interface TaskActionRowButtonProps {
  task: Task;
  showMarkComplete?: boolean;
}

export function TaskActionRowButton({ task, showMarkComplete = false }: TaskActionRowButtonProps) {
  const openTaskAction = useOpenTaskAction();
  const markComplete = useMarkCompleteTask(task);
  const { taskAction, id: taskId, status } = task;

  // Don't show any button if task status is in NO_ACTION_STATUSES
  if (NO_ACTION_STATUSES.includes(status)) {
    return null;
  }

  // If task has a task action, show the task action button
  if (taskAction) {
    const label = taskActionLabel(taskAction);
    return (
      <TaskActionButton onClick={() => openTaskAction(taskId, taskAction)}>
        {label}
      </TaskActionButton>
    );
  }

  // If showMarkComplete is enabled and task is not in NO_ACTION_STATUSES
  if (showMarkComplete) {
    return (
      <TaskActionButton onClick={() => markComplete()}>
        Mark Complete
      </TaskActionButton>
    );
  }

  return null;
}

export function TaskActionMenuButton({ taskId, taskAction }: { taskId: TaskId, taskAction: Task["taskAction"] }) {
  const openTaskAction = useOpenTaskAction();
  const label = taskActionLabel(taskAction);

  return (
    <MenuItem onClick={() => openTaskAction(taskId, taskAction)} icon={<MenuItemIcon icon={MdOutlineTouchApp} />}>
      {label}
    </MenuItem>
  );
}
